import type {
  blockedTeamPatchSchema,
  bulkTeamActionRequestSchema,
  bulkTeamActionResponseSchema,
  importTeamSearchResultSchema,
  importTeamsRequestSchema,
  membershipBulkDeleteRequestSchema,
  membershipBulkUpdateRequestSchema,
  membershipResponseSchema,
  membershipUpdateRestRequestSchema,
  membershipWithResourceAndSchemesResponseSchema,
  openPositionSummarySchema,
  portfolioChangeEligibilityResponseSchema,
  skillSummarySchema,
  syncStatusResponseSchema,
  teamBulkCreateItemRequestSchema,
  teamBulkCreateRequestSchema,
  teamBulkCreateResponseSchema,
  teamBulkPatchRequestSchema,
  teamBulkPatchResponseSchema,
  teamCreateRequestSchema,
  teamLookupResponseSchema,
  teamMemberResponseSchema,
  teamPatchRequestSchema,
  teamSchema,
  teamsSyncRequestSchema,
} from '@tempo-io/capacity-dto';
import { z } from 'zod';
import type { EmploymentType, Resource } from './capacity/resources';

export type TeamSourceType = 'JIRA_TEAM' | 'TEMPO_TEAM';

export type CreateTeamRequest = z.infer<typeof teamCreateRequestSchema>;

export type TeamPatchRequest = z.infer<typeof teamPatchRequestSchema>;

// `memberNames` has a backend default (`emptyList()`); the schema generator over-promotes it to
// required. Softened back to optional to match the API's actual contract.
type TeamBulkCreateItemBase = z.infer<typeof teamBulkCreateItemRequestSchema>;
export type BulkCreateTeamItem = Omit<TeamBulkCreateItemBase, 'memberNames'> & {
  memberNames?: TeamBulkCreateItemBase['memberNames'];
};

export type BulkCreateTeamsRequest = Omit<z.infer<typeof teamBulkCreateRequestSchema>, 'teams'> & {
  teams: BulkCreateTeamItem[];
};

export type BulkCreateTeamsResponse = z.infer<typeof teamBulkCreateResponseSchema>;

// `color`, `icon`, `totalCapacitySeconds` and nested `memberships` are optional frontend-side
// additions on top of the generated team record; the capacity API does not return them on the base
// team schema (`totalCapacitySeconds` is served by the team-lookup endpoint instead).
export type Team = z.infer<typeof teamSchema> & {
  icon?: string;
  color?: string;
  totalCapacitySeconds?: number | null;
  memberships?: TeamMembershipWithResource[];
};

// Kept local: this type is used both for the membership-with-team API response and for
// client-side editable/synthetic memberships, which do not carry the backend-only
// `commitmentOverridden`/`sourceCommitment` fields that `membershipWithTeamResponseSchema` requires.
export type ResourceTeamMembership = {
  membershipId: number;
  resourceId: number;
  teamId: number;
  teamName: string;
  startDate?: string;
  endDate?: string;
  commitment?: number;
  sourceCommitment?: number;
  commitmentOverridden?: boolean;
  roleId?: number;
  roleName?: string;
  membershipSourceId?: string;
  teamSourceType?: TeamSourceType;
};

export type PendingMembershipChanges = {
  [membershipId: number]: Partial<MembershipUpdateRequest>;
};

export type MembershipUpdateRequest = z.infer<typeof membershipUpdateRestRequestSchema>;

export type MembershipBulkUpdateRequest = z.infer<typeof membershipBulkUpdateRequestSchema>;

type TeamMembershipRequest = MembershipUpdateRequest & {
  teamId: number;
};

// Kept local: memberships are added from client-side pending memberships whose `commitment` is
// optional, whereas the generated `teamMembershipRequestSchema` requires it.
export type MembershipBulkAddRequest = {
  memberships: TeamMembershipRequest[];
};

export type MembershipBulkDeleteRequest = z.infer<typeof membershipBulkDeleteRequestSchema>;

export type BulkPatchTeamsRequest = z.infer<typeof teamBulkPatchRequestSchema>;

export type PortfolioChangeEligibility = z.infer<typeof portfolioChangeEligibilityResponseSchema>;

export type BlockedTeamPatch = z.infer<typeof blockedTeamPatchSchema>;

export type BulkPatchTeamsResponse = z.infer<typeof teamBulkPatchResponseSchema>;

export type BulkTeamActionRequest = z.infer<typeof bulkTeamActionRequestSchema>;

export type MembershipResponse = z.infer<typeof membershipResponseSchema>;

export type BulkTeamActionResponse = z.infer<typeof bulkTeamActionResponseSchema>;

export type TeamMemberWithCapacity = z.infer<typeof teamMemberResponseSchema>;

export type OpenPositionSkillSummary = z.infer<typeof skillSummarySchema>;

export type OpenPositionSummary = z.infer<typeof openPositionSummarySchema>;

export type TeamLookupResponse = z.infer<typeof teamLookupResponseSchema>;

export type TeamsLookupResponse = {
  data: TeamLookupResponse[];
};

export type TeamsResponse = {
  data: Team[];
};

export type StatusAction = 'DEACTIVATE' | 'REACTIVATE';
export const STATUS_ACTION = {
  DEACTIVATE: 'DEACTIVATE' as StatusAction,
  REACTIVATE: 'REACTIVATE' as StatusAction,
};

export type ResourceStatus = 'ACTIVE' | 'DEACTIVATED';
export const RESOURCE_STATUS = {
  ACTIVE: 'ACTIVE' as ResourceStatus,
  DEACTIVATED: 'DEACTIVATED' as ResourceStatus,
};
export type ResourceFilterStatus = ResourceStatus | 'ALL';

export type SearchMembershipsSearchParams = {
  query?: string;
  resourceStatus?: ResourceFilterStatus;
  roleId?: number[];
  skillId?: number[];
  locationId?: number[];
  employmentType?: EmploymentType[];
  showPastMemberships?: boolean;
  showFutureMemberships?: boolean;
};

type MembershipWithResourceBase = z.infer<typeof membershipWithResourceAndSchemesResponseSchema>;

// A membership either belongs to an open position (both fields present) or to a real resource
// (both absent). The generated schema types the two fields as independently nullish, so the
// invariant is restored here as a discriminated union to keep narrowing sound in UI code.
type OpenPositionMembershipFields =
  | { openPositionId?: null; openPositionName?: null }
  | { openPositionId: number; openPositionName: string };

export type TeamMembershipWithResource = Omit<
  MembershipWithResourceBase,
  'openPositionId' | 'openPositionName'
> &
  OpenPositionMembershipFields;

export interface TeamWithIncludedResponse {
  team: Team;
  memberships?: TeamMembershipWithResource[];
}

const CREATE_MODE = 'CREATE';
export const EDIT_MODE = 'EDIT';
export type TeamFormMode = typeof CREATE_MODE | typeof EDIT_MODE;

export interface TeamRequestWithMembers extends Omit<CreateTeamRequest, 'loopId'> {
  loopId?: number | null;
  members?: Resource[];
}

export type ImportTeamsSearch = z.infer<typeof importTeamSearchResultSchema>;

export interface ImportTeamsSearchResponse {
  data: ImportTeamsSearch[];
}

export interface ImportTeamsSearchParameters {
  sourceType: TeamSourceType;
  instanceId: string;
}

export type ImportTeamsRequest = z.infer<typeof importTeamsRequestSchema>;

interface DateRangeInput {
  from: string;
  to: string;
}

export type TeamLookupExpand = 'MEMBERSHIPS' | 'CAPACITY' | 'SKILLS' | 'OPEN_POSITIONS';

export interface TeamsLookupRequest {
  ids?: number[];
  dateRange: DateRangeInput;
  teamRoleIds?: number[];
  skillIds?: number[];
  loopId?: string;
  expand?: TeamLookupExpand[];
}

export type SyncTeamsRequest = z.infer<typeof teamsSyncRequestSchema>;

export type SyncStatus = z.infer<typeof syncStatusResponseSchema>;

export interface SyncStatusResponse {
  data: SyncStatus[];
}

export const TEAM = 'TEAM' as const;
export const SKELETON = 'SKELETON' as const;
export type TeamSkeletonRow = { type: typeof SKELETON; id: string };
export type TeamRow =
  | { type: typeof TEAM; id: string; team: Team; isSyncing: boolean }
  | TeamSkeletonRow;

export type ResourceFormData = {
  name: string;
  email: string;
  employmentType: EmploymentType;
};

export interface PendingNewMembership {
  teamId: number;
  teamName: string;
  tempId: string;
  commitment?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  roleId?: number | null;
}

export const TEAM_SORT_FIELD = {
  ID: 'ID',
  NAME: 'NAME',
  DESCRIPTION: 'DESCRIPTION',
  CREATED_AT: 'CREATED_AT',
  UPDATED_AT: 'UPDATED_AT',
  MEMBERSHIP_COUNT: 'MEMBERSHIP_COUNT',
} as const;

type TeamSortField = (typeof TEAM_SORT_FIELD)[keyof typeof TEAM_SORT_FIELD];

export const SORT_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;

type SortDirection = (typeof SORT_DIRECTION)[keyof typeof SORT_DIRECTION];

export type TeamSearchParams = {
  query?: string;
  sortBy?: TeamSortField;
  sortDirection?: SortDirection;
  limit?: number;
  cursor?: string;
  roleIds?: number[];
  loopIds?: string[];
  skillIds?: number[];
  sourceTypes?: (TeamSourceType | null)[];
};

export const membershipOverlapConflictSchema = z.object({
  resourceId: z.number(),
  existingStartDate: z.string().nullable(),
  existingEndDate: z.string().nullable(),
});

export type MembershipOverlapConflict = z.infer<typeof membershipOverlapConflictSchema>;

const allocationOutcomeSchema = z.enum(['HEADROOM_USED', 'AUTO_SPLIT', 'NO_HEADROOM']);

export type AllocationOutcome = z.infer<typeof allocationOutcomeSchema>;

const affectedSiblingSchema = z.object({
  membershipId: z.number(),
  teamId: z.number(),
  teamName: z.string().nullable(),
  currentCommitment: z.number(),
  newCommitment: z.number(),
});

export type AffectedSibling = z.infer<typeof affectedSiblingSchema>;

export const membershipPreviewResponseSchema = z.object({
  proposedCommitment: z.number(),
  outcome: allocationOutcomeSchema,
  affectedSiblings: z.array(affectedSiblingSchema),
  adjustedSyncedMemberships: z.array(affectedSiblingSchema),
  currentOtherTotal: z.number(),
});

export type MembershipPreviewResponse = z.infer<typeof membershipPreviewResponseSchema>;

export const membershipOverridePreviewResponseSchema = z.object({
  membershipId: z.number(),
  proposedCommitment: z.number(),
  revertingToSource: z.boolean(),
  adjustedSyncedMemberships: z.array(affectedSiblingSchema),
});

export type MembershipOverridePreviewResponse = z.infer<
  typeof membershipOverridePreviewResponseSchema
>;

export const allocationErrorSchema = z.object({
  code: z.literal('ALLOCATION_EXCEEDED'),
  message: z.string(),
  memberId: z.number(),
  currentTotal: z.number(),
  attemptedTotal: z.number(),
  oldTotal: z.number(),
  bindingSegment: z.string().optional(),
});

export type AllocationError = z.infer<typeof allocationErrorSchema>;

// The capacity service omits every pagination field when a request is unpaginated
// (`@JsonInclude(NON_NULL)` on its `PageResponse`), so none of them are guaranteed to arrive.
export type TeamsPageResponse = {
  data: Team[];
  recordLimit?: number | null;
  recordCount?: number;
  cursor?: string | null;
  hasNextPage?: boolean;
};
